import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Bell, Plus, LayoutGrid, Repeat, BarChart3, Wrench, Clock, Settings, HelpCircle, FileText, ShieldAlert, Download, Mail, X, User, CreditCard, LogOut, ChevronDown, Link2, Menu } from 'lucide-react';
import { api } from '../lib/api';

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040',
  facebook: '#1877f2', grailed: '#1a1a1a', mercari: '#ff5722', poshmark: '#7b1fa2',
  shopify: '#95bf47', whatnot: '#ffc940', bigcommerce: '#34313f', woocommerce: '#96588a',
};

export function DashboardLayout() {
  const navigate = useNavigate();
  const [dismissedBanner, setDismissedBanner] = useState(false);
  const [resending, setResending] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me').then((r) => r.data.user),
  });

  const { data: connectedPlatforms = [] } = useQuery({
    queryKey: ['platforms-sidebar'],
    queryFn: () => api.get('/platforms').then((r) => r.data.filter((p: any) => p.connected)),
    enabled: !!me?.id,
    staleTime: 5 * 60 * 1000,
  });

  async function resendVerification() {
    setResending(true);
    try {
      await api.post('/auth/resend-verification');
      alert('Verification email sent! Check your inbox.');
    } catch {
      alert('Failed to send email. Try again.');
    } finally {
      setResending(false);
    }
  }

  const showVerificationBanner = !dismissedBanner && me && !me.emailVerifiedAt;

  // Unread activity count — events since last time user visited /dashboard/activity
  const LAST_VIEWED_KEY = me?.id ? `crosova_activity_viewed_${me.id}` : null;
  const lastViewed = LAST_VIEWED_KEY ? (localStorage.getItem(LAST_VIEWED_KEY) ?? undefined) : undefined;

  const { data: activityCount } = useQuery({
    queryKey: ['audit-count', lastViewed],
    queryFn: () => api.get('/audit/count', { params: lastViewed ? { since: lastViewed } : {} }).then((r) => r.data.count),
    enabled: !!me?.id,
    refetchInterval: 60_000,
  });

  const unreadCount = typeof activityCount === 'number' && activityCount > 0
    ? activityCount > 9 ? '9+' : String(activityCount)
    : undefined;

  const initials = me?.name ? me.name.slice(0, 1).toUpperCase() : '?';

  return (
    <div className="flex flex-col h-screen bg-bg-base">
      {/* Email verification banner */}
      {showVerificationBanner && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[11.5px] text-amber-300 min-w-0">
            <Mail size={13} className="flex-shrink-0" />
            <span className="truncate">Verify your email to secure your account.</span>
            <button
              onClick={resendVerification}
              disabled={resending}
              className="underline hover:text-amber-200 transition-colors flex-shrink-0"
            >
              {resending ? 'Sending...' : 'Resend'}
            </button>
          </div>
          <button onClick={() => setDismissedBanner(true)} className="text-amber-400/50 hover:text-amber-400 flex-shrink-0">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Top bar */}
      <header className="h-[52px] bg-bg-surface border-b border-white/[0.06] flex items-center justify-between px-4 gap-2">
        <div className="flex items-center gap-2.5">
          {/* Hamburger — mobile only */}
          <button
            className="md:hidden w-[30px] h-[30px] flex items-center justify-center text-white/40 hover:text-white/70 transition-colors"
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu size={18} />
          </button>
          <div className="w-[30px] h-[30px] bg-primary-500 rounded-lg flex items-center justify-center font-bold text-xs tracking-tight text-white flex-shrink-0">
            Cr
          </div>
          <div className="text-[15px] font-semibold tracking-tight">
            Cros<span className="text-secondary-400">ova</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Search — hidden on mobile */}
          <div className="hidden md:flex items-center gap-2 bg-white/[0.05] border border-white/[0.07] rounded-lg px-2.5 py-1">
            <Search size={13} className="text-white/30 flex-shrink-0" />
            <input
              className="bg-transparent text-xs text-white/70 placeholder-white/25 outline-none w-36"
              placeholder="Search listings..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const q = (e.target as HTMLInputElement).value.trim();
                  if (q) navigate(`/dashboard/inventory?search=${encodeURIComponent(q)}`);
                }
              }}
            />
          </div>
          <button
            onClick={() => navigate('/dashboard/activity')}
            className="w-[30px] h-[30px] bg-white/[0.05] border border-white/[0.07] rounded-lg flex items-center justify-center text-white/40 hover:text-white/70 transition-colors relative"
            title="Activity"
          >
            <Bell size={14} />
            {unreadCount && (
              <div className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-orange-500 rounded-full flex items-center justify-center text-[8px] font-bold text-white px-0.5">
                {unreadCount}
              </div>
            )}
          </button>
          <button onClick={() => navigate('/dashboard/new')} className="btn-primary flex items-center gap-1.5 !py-1.5">
            <Plus size={13} /> <span className="hidden sm:inline">New listing</span>
          </button>
          {/* Avatar dropdown */}
          <div ref={avatarRef} className="relative">
            <button
              onClick={() => setAvatarOpen(!avatarOpen)}
              className="flex items-center gap-1.5 rounded-full pl-0.5 pr-1.5 py-0.5 hover:bg-white/[0.06] transition-colors"
            >
              <div className="w-[28px] h-[28px] rounded-full bg-gradient-to-br from-secondary-400 to-primary-500 flex items-center justify-center text-xs font-semibold text-white flex-shrink-0">
                {initials}
              </div>
              <ChevronDown size={11} className={`text-white/30 transition-transform ${avatarOpen ? 'rotate-180' : ''}`} />
            </button>

            {avatarOpen && (
              <div className="absolute right-0 top-[calc(100%+6px)] w-56 max-w-[calc(100vw-16px)] bg-bg-raised border border-white/[0.1] rounded-xl shadow-2xl z-50 overflow-hidden">
                {/* User info header */}
                <div className="px-4 py-3 border-b border-white/[0.06]">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary-400 to-primary-500 flex items-center justify-center text-xs font-semibold text-white flex-shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium truncate">{me?.name ?? ''}</div>
                      <div className="text-[11px] text-white/35 truncate">{me?.email ?? ''}</div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="text-[10px] bg-primary-500/10 text-primary-300 border border-primary-500/20 px-2 py-0.5 rounded-full capitalize">
                      {me?.plan ?? 'trial'} plan
                    </span>
                  </div>
                </div>

                {/* Menu items */}
                <div className="p-1">
                  <Link
                    to="/dashboard/settings?tab=profile"
                    onClick={() => setAvatarOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] text-white/60 hover:bg-white/[0.05] hover:text-white transition-colors"
                  >
                    <User size={13} /> Profile & security
                  </Link>
                  <Link
                    to="/dashboard/settings?tab=billing"
                    onClick={() => setAvatarOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] text-white/60 hover:bg-white/[0.05] hover:text-white transition-colors"
                  >
                    <CreditCard size={13} /> Billing & plan
                  </Link>
                  <Link
                    to="/dashboard/settings?tab=platforms"
                    onClick={() => setAvatarOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] text-white/60 hover:bg-white/[0.05] hover:text-white transition-colors"
                  >
                    <Link2 size={13} /> Connect platforms
                  </Link>
                  {me?.isAdmin && (
                    <Link
                      to="/dashboard/admin"
                      onClick={() => setAvatarOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] text-white/60 hover:bg-white/[0.05] hover:text-white transition-colors"
                    >
                      <ShieldAlert size={13} /> Admin panel
                    </Link>
                  )}
                </div>

                <div className="p-1 border-t border-white/[0.06]">
                  <button
                    onClick={() => api.post('/auth/logout').then(() => { window.location.href = '/login'; })}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] text-danger/80 hover:bg-danger/[0.06] hover:text-danger transition-colors"
                  >
                    <LogOut size={13} /> Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile backdrop */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-40 md:hidden"
            onClick={() => setMobileNavOpen(false)}
          />
        )}

        {/* Sidebar — mobile: fixed overlay drawer; desktop: static */}
        <aside className={`
          fixed top-0 left-0 h-full z-50 flex flex-col
          w-[220px] bg-bg-surface border-r border-white/[0.05] p-3
          transition-transform duration-200 ease-in-out
          ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}
          md:relative md:translate-x-0 md:w-[186px] md:z-auto md:flex
        `}>
          {/* Mobile close button */}
          <button
            className="md:hidden self-end mb-2 text-white/30 hover:text-white/60 transition-colors"
            onClick={() => setMobileNavOpen(false)}
          >
            <X size={16} />
          </button>

          <NavSection label="Main">
            <NavItem to="/dashboard/inventory" icon={LayoutGrid} onNav={() => setMobileNavOpen(false)}>Inventory</NavItem>
            <NavItem to="/dashboard/import" icon={Download} onNav={() => setMobileNavOpen(false)}>Import</NavItem>
            <NavItem to="/dashboard/crosslist" icon={Repeat} onNav={() => setMobileNavOpen(false)}>Crosslist</NavItem>
            <NavItem to="/dashboard/analytics" icon={BarChart3} onNav={() => setMobileNavOpen(false)}>Analytics</NavItem>
            <NavItem to="/dashboard/tools" icon={Wrench} onNav={() => setMobileNavOpen(false)}>Tools</NavItem>
            <NavItem to="/dashboard/templates" icon={FileText} onNav={() => setMobileNavOpen(false)}>Templates</NavItem>
            <NavItem to="/dashboard/activity" icon={Clock} badge={unreadCount} onNav={() => setMobileNavOpen(false)}>Activity</NavItem>
          </NavSection>

          <NavSection label="Platforms">
            {connectedPlatforms.length === 0 ? (
              <NavLink
                to="/dashboard/settings?tab=platforms"
                onClick={() => setMobileNavOpen(false)}
                className="flex items-center gap-1.5 px-2 py-[5px] rounded-md text-[11px] text-white/25 hover:text-white/50 hover:bg-white/[0.03] transition-colors"
              >
                + Connect a platform
              </NavLink>
            ) : (
              <div className="flex flex-col gap-0.5">
                {connectedPlatforms.map((p: any) => (
                  <div key={p.platform} className="flex items-center gap-1.5 px-2 py-[3px] rounded hover:bg-white/[0.03] cursor-pointer">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: PLATFORM_COLOURS[p.platform] ?? '#555' }} />
                    <span className="text-[11px] text-white/50 capitalize">{p.platform}</span>
                  </div>
                ))}
              </div>
            )}
          </NavSection>

          <NavSection label="Account">
            <NavItem to="/dashboard/settings" icon={Settings} onNav={() => setMobileNavOpen(false)}>Settings</NavItem>
            <NavItem to="/dashboard/help" icon={HelpCircle} onNav={() => setMobileNavOpen(false)}>Help</NavItem>
            {me?.isAdmin && (
              <NavItem to="/dashboard/admin" icon={ShieldAlert} onNav={() => setMobileNavOpen(false)}>Admin</NavItem>
            )}
          </NavSection>
        </aside>

        {/* Main */}
        <main className="flex-1 overflow-auto p-4 md:p-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3.5">
      <div className="text-[9px] font-semibold text-white/20 uppercase tracking-[1px] px-2 mb-1">{label}</div>
      {children}
    </div>
  );
}

function NavItem({
  to, icon: Icon, children, badge, onNav,
}: { to: string; icon: React.ComponentType<{ size?: number | string }>; children: React.ReactNode; badge?: string; onNav?: () => void }) {
  return (
    <NavLink
      to={to}
      onClick={onNav}
      className={({ isActive }) =>
        `flex items-center gap-2 px-2 py-[5px] rounded-md text-[11.5px] cursor-pointer mb-px transition-colors ${
          isActive ? 'bg-primary-500/[0.15] text-primary-300' : 'text-white/40 hover:bg-white/[0.03]'
        }`
      }
    >
      <Icon size={14} />
      <span>{children}</span>
      {badge && <span className="ml-auto bg-orange-500/20 text-orange-400 text-[9px] font-semibold px-1.5 py-px rounded">{badge}</span>}
    </NavLink>
  );
}
